/**
 * Store search helpers (business/Shopify#54, build 1, 14/10). Loaded with defer on every page (layout/theme.liquid);
 * small. Two jobs:
 *
 * 1. Products in the main results: any search form submitted without a "type" gets type=product (owner pick:
 *    products only in the main results; articles sit in the "Guides (n)" tab on the results page).
 *
 * 2. Partial part numbers in the predictive dropdown: Shopify's search matches whole cross-reference numbers only
 *    ("z418" finds OXF418, "z41" finds nothing). Our own index, assets/spo-xref-index.json (Ryco / Wesfil / Sakura /
 *    MANN numbers -> product, generated by scripts/gen-spo-search-data.ts from interchangeable.*), is fetched once,
 *    on the first focus of a search box, never on page load. Each time the dropdown draws its results
 *    (snippets/spo-predictive-best.liquid leaves a [data-spo-xref-slot]), the numbers starting with what was typed
 *    are listed as options ("Ryco Z411 -> OXF411"), keyboard-reachable with the dropdown's own arrow keys.
 * Read only. No dependencies.
 */
(() => {
  if (window.spoSearch) return;
  const script = document.currentScript;
  const INDEX_URL = script?.dataset.index || '';
  const MAX = 6;

  /** @param {string} s */
  const norm = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  /* 1. type=product */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = new URL(form.action || location.href, location.origin);
      if (action.pathname !== '/search' || form.querySelector('[name="type"]')) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'type';
      input.value = 'product';
      form.append(input);
    },
    true
  );

  /* 2. Cross-reference index */
  /** @type {Promise<{brands: Record<string,string>, p: string[], list: {n: string, key: string, brand: string, i: number}[]}> | null} */
  let indexPromise = null;
  const loadIndex = () => {
    if (!indexPromise && INDEX_URL) {
      indexPromise = fetch(INDEX_URL)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
        .then((data) => {
          const list = [];
          for (const [b, joined] of Object.entries(data.x || {})) {
            for (const entry of String(joined).split('|')) {
              const at = entry.lastIndexOf(' ');
              if (at < 1) continue;
              const n = entry.slice(0, at);
              list.push({ n, key: norm(n), brand: data.brands?.[b] || b, i: Number(entry.slice(at + 1)) });
            }
          }
          return { brands: data.brands || {}, p: data.p || [], list };
        })
        .catch(() => {
          indexPromise = null;
          return null;
        });
    }
    return indexPromise;
  };
  document.addEventListener(
    'focusin',
    (event) => {
      const el = event.target;
      if (el instanceof HTMLInputElement && el.name === 'q') loadIndex();
    },
    { passive: true }
  );

  /** Fill one dropdown slot. @param {HTMLElement} slot */
  const fill = async (slot) => {
    if (slot.dataset.filled) return;
    slot.dataset.filled = '1';
    const term = norm(slot.dataset.terms);
    // A part-number prefix: 2+ characters with a digit ("z41", "wz4", "c-80").
    if (term.length < 2 || !/\d/.test(term)) return;
    const index = await loadIndex();
    if (!index || !slot.isConnected) return;
    const shown = new Set(
      [...(slot.closest('[data-spo-ps-best]')?.querySelectorAll('[data-spo-handle]') || [])].map((li) => li.getAttribute('data-spo-handle'))
    );
    // One row per brand + number; when several of our parts carry it (single and multi-packs), the shortest
    // handle (the single) is linked.
    const best = new Map();
    for (const e of index.list) {
      if (!e.key.startsWith(term)) continue;
      const [handle, sku] = String(index.p[e.i] || '').split('~');
      if (!handle || shown.has(handle)) continue;
      const k = e.brand + e.key;
      const prev = best.get(k);
      if (!prev || handle.length < prev.handle.length) best.set(k, { ...e, handle, sku: sku || handle.toUpperCase() });
    }
    const hits = [...best.values()];
    // Exact number first, then shortest numbers (closest to what was typed), then alphabetical.
    hits.sort((a, b) => (a.key === term ? -1 : 0) - (b.key === term ? -1 : 0) || a.key.length - b.key.length || a.key.localeCompare(b.key));
    if (!hits.length) return;
    const top = hits.slice(0, MAX);
    const id = 'predictive-search-xref';
    const h = document.createElement('h6');
    h.id = id;
    h.className = 'predictive-search__heading body-m-font-size';
    h.textContent = 'Part numbers';
    const ul = document.createElement('ul');
    ul.className = 'predictive-search__results-list list-unstyled spo-ps-best__list';
    ul.setAttribute('role', 'listbox');
    ul.setAttribute('aria-labelledby', id);
    top.forEach((hit, n) => {
      const li = document.createElement('li');
      li.id = `spo-ps-xref-${n}`;
      li.className = 'predictive-search__list-item spo-ps-best__item';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      const a = document.createElement('a');
      a.className = 'spo-ps-best__link';
      a.href = `/products/${encodeURIComponent(hit.handle)}`;
      a.tabIndex = -1;
      const from = document.createElement('span');
      from.className = 'spo-ps-best__from';
      from.textContent = `${hit.brand} ${hit.n}`;
      const arrow = document.createElement('span');
      arrow.className = 'spo-ps-best__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = ' is replaced by ';
      const to = document.createElement('span');
      to.className = 'spo-ps-best__to';
      to.textContent = hit.sku;
      a.append(from, arrow, sr, to);
      li.append(a);
      ul.append(li);
    });
    slot.replaceChildren(h, ul);
    if (hits.length > MAX) {
      const more = document.createElement('p');
      more.className = 'spo-ps-best__hint';
      more.textContent = `${hits.length - MAX} more numbers start with "${slot.dataset.terms}": keep typing.`;
      slot.append(more);
    }
    // Tell screen readers about the added options (the dropdown's own count was announced before this).
    const status = slot.closest('predictive-search')?.querySelector('.predictive-search-status');
    if (status) {
      status.setAttribute('aria-hidden', 'false');
      status.textContent = `${top.length} part number ${top.length === 1 ? 'match' : 'matches'}`;
    }
  };

  const scan = () => {
    for (const slot of document.querySelectorAll('[data-spo-xref-slot]:not([data-filled])')) fill(slot);
  };
  new MutationObserver((records) => {
    for (const r of records) {
      if (r.addedNodes.length) {
        scan();
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.spoSearch = { loadIndex };
})();
